import { useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';

// All Whisper-supported languages with native names + search aliases
const LANGUAGES = [
  { code: 'auto', native: 'Auto', label: 'Detection automatique' },
  { code: 'fr', native: 'Francais', label: 'French' },
  { code: 'en', native: 'English', label: 'Anglais' },
  { code: 'es', native: 'Espanol', label: 'Spanish Espagnol' },
  { code: 'de', native: 'Deutsch', label: 'German Allemand' },
  { code: 'it', native: 'Italiano', label: 'Italian Italien' },
  { code: 'pt', native: 'Portugues', label: 'Portuguese Portugais' },
  { code: 'nl', native: 'Nederlands', label: 'Dutch Neerlandais' },
  { code: 'ru', native: 'Russkij', label: 'Russian Russe' },
  { code: 'ar', native: 'Al-Arabiyyah', label: 'Arabic Arabe' },
  { code: 'zh', native: 'Zhongwen', label: 'Chinese Chinois' },
  { code: 'ja', native: 'Nihongo', label: 'Japanese Japonais' },
  { code: 'ko', native: 'Hangugeo', label: 'Korean Coreen' },
  { code: 'hi', native: 'Hindi', label: 'Hindi' },
  { code: 'tr', native: 'Turkce', label: 'Turkish Turc' },
  { code: 'pl', native: 'Polski', label: 'Polish Polonais' },
  { code: 'sr', native: 'Srpski', label: 'Serbian Serbe' },
  { code: 'hr', native: 'Hrvatski', label: 'Croatian Croate' },
  { code: 'bs', native: 'Bosanski', label: 'Bosnian Bosniaque' },
  { code: 'ro', native: 'Romana', label: 'Romanian Roumain' },
  { code: 'cs', native: 'Cestina', label: 'Czech Tcheque' },
  { code: 'sk', native: 'Slovencina', label: 'Slovak Slovaque' },
  { code: 'sl', native: 'Slovenscina', label: 'Slovenian Slovene' },
  { code: 'bg', native: 'Bulgarski', label: 'Bulgarian Bulgare' },
  { code: 'uk', native: 'Ukrainska', label: 'Ukrainian Ukrainien' },
  { code: 'el', native: 'Ellinika', label: 'Greek Grec' },
  { code: 'hu', native: 'Magyar', label: 'Hungarian Hongrois' },
  { code: 'sv', native: 'Svenska', label: 'Swedish Suedois' },
  { code: 'da', native: 'Dansk', label: 'Danish Danois' },
  { code: 'no', native: 'Norsk', label: 'Norwegian Norvegien' },
  { code: 'fi', native: 'Suomi', label: 'Finnish Finnois' },
  { code: 'he', native: 'Ivrit', label: 'Hebrew Hebreu' },
  { code: 'th', native: 'Phasathai', label: 'Thai Thailandais' },
  { code: 'vi', native: 'Tieng Viet', label: 'Vietnamese Vietnamien' },
  { code: 'id', native: 'Bahasa Indonesia', label: 'Indonesian Indonesien' },
  { code: 'ms', native: 'Bahasa Melayu', label: 'Malay Malais' },
  { code: 'tl', native: 'Tagalog', label: 'Tagalog Filipino' },
  { code: 'sw', native: 'Kiswahili', label: 'Swahili' },
  { code: 'af', native: 'Afrikaans', label: 'Afrikaans' },
  { code: 'ca', native: 'Catala', label: 'Catalan' },
  { code: 'gl', native: 'Galego', label: 'Galician Galicien' },
  { code: 'et', native: 'Eesti', label: 'Estonian Estonien' },
  { code: 'lv', native: 'Latviesu', label: 'Latvian Letton' },
  { code: 'lt', native: 'Lietuviu', label: 'Lithuanian Lituanien' },
  { code: 'mk', native: 'Makedonski', label: 'Macedonian Macedonien' },
  { code: 'is', native: 'Islenska', label: 'Icelandic Islandais' },
  { code: 'cy', native: 'Cymraeg', label: 'Welsh Gallois' },
  { code: 'be', native: 'Belaruskaja', label: 'Belarusian Bielorusse' },
  { code: 'az', native: 'Azerbaycanca', label: 'Azerbaijani' },
  { code: 'hy', native: 'Hayeren', label: 'Armenian Armenien' },
  { code: 'ka', native: 'Kartuli', label: 'Georgian Georgien' },
  { code: 'kk', native: 'Qazaqsha', label: 'Kazakh' },
  { code: 'ur', native: 'Urdu', label: 'Urdu Ourdou' },
  { code: 'fa', native: 'Farsi', label: 'Persian Persan' },
  { code: 'ta', native: 'Tamil', label: 'Tamil Tamoul' },
  { code: 'mr', native: 'Marathi', label: 'Marathi' },
  { code: 'kn', native: 'Kannada', label: 'Kannada' },
  { code: 'ne', native: 'Nepali', label: 'Nepali Nepalais' },
  { code: 'mi', native: 'Te Reo Maori', label: 'Maori' },
];

export function LanguageCombobox({ value, onSelect, testId = 'voice-language-select' }) {
  const [open, setOpen] = useState(false);

  const selected = LANGUAGES.find(l => l.code === value) || LANGUAGES[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-7 text-xs justify-between gap-1.5 px-2 min-w-[110px] max-w-[150px]"
          data-testid={testId}
        >
          <Globe className="w-3 h-3 shrink-0 text-muted-foreground" />
          <span className="truncate">{selected.native}</span>
          <ChevronsUpDown className="w-3 h-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Rechercher une langue..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-3 text-xs">Aucune langue trouvee.</CommandEmpty>
            <CommandGroup>
              {LANGUAGES.map((lang) => (
                <CommandItem
                  key={lang.code}
                  value={`${lang.native} ${lang.label} ${lang.code}`}
                  onSelect={() => {
                    onSelect(lang.code);
                    setOpen(false);
                  }}
                  className="text-xs"
                  data-testid={`${testId}-${lang.code}`}
                >
                  <Check
                    className={cn(
                      'w-3 h-3 shrink-0',
                      value === lang.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span>{lang.native}</span>
                  {lang.code !== 'auto' && (
                    <span className="ml-auto text-muted-foreground text-[10px] uppercase">{lang.code}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
